//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  IMediaItemWithLike2BuyV2Status,
  ILike2BuyV2MediaItem,
  Like2BuyV2ModerationItemFilter,
  NormalizedLike2BuyV2Items,
  Like2BuyV2ModerationStatus,
} from '@apis/like2buy-v2/like2BuyV2ModerationModel';
import { Like2BuyV2TemplateConfig } from '@apis/like2buy-v2/like2BuyV2StoreModel';
import { MediaTypeOverlayIconType } from '@components/atoms/overlay-icon/MediaTypeOverlayIcon';
import { IActionSheetButton } from '@components/molecules/action-sheet/ActionSheet';
import { IInstagramLike2BuyV2Customizations } from '@instagram-account/like2buy-v2/constants/like2BuyV2InstagramConstants';
import {
  instagramCustomizationsToStoreConfig,
  instagramStoreConfigToCustomizations,
  isInstagramLike2BuyV2Customizations,
  isInstagramLike2BuyV2Template,
} from '@instagram-account/like2buy-v2/utils/instagramLike2BuyV2CustomizationsUtils';
import { REQUIRED_LIKE2BUY_DISTRIBUTION_CHANNEL } from '@like2buy-v2/Like2BuyV2UIConstants';
import { InstagramMediaProductType } from '@models/IInstagramMediaItem';
import { INetworkItem } from '@models/INetworkItem';
import MediaType from '@models/MediaType';
import { SocialNetwork } from '@models/SocialNetwork';
import { hasPermissionForTask } from '@services/permissions';
import {
  isTiktokLike2BuyV2Customizations,
  isTiktokLike2BuyV2Template,
} from '@tiktok/components/like2buy/customizations/storeCustomizationsConstants';
import { ITikTokLike2BuyCustomizations } from '@tiktok/redux/like2buy/customizations/tiktokLike2BuyCustomizationsModel';
import {
  tiktokCustomizationsToStoreConfig,
  tiktokStoreConfigToCustomizations,
} from '@tiktok/redux/like2buy/customizations/tiktokLike2BuyCustomizationsUtils';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import normalize from '@utils/normalize';

export const DEFAULT_LIKE2BUY_V2_FILTER =
  Like2BuyV2ModerationItemFilter.AllMedia;

const toLike2BuyV2MediaItem = (
  item: IMediaItemWithLike2BuyV2Status,
): ILike2BuyV2MediaItem => ({
  ...item.item,
  like2BuyV2ModerationStatus: item.status,
});

export const toLike2BuyV2MediaItems = (
  mediaItems: IMediaItemWithLike2BuyV2Status[],
): ILike2BuyV2MediaItem[] => mediaItems.map(toLike2BuyV2MediaItem);

export const toLike2BuyV2NormalizedMediaItems = (
  mediaItems: IMediaItemWithLike2BuyV2Status[],
): NormalizedLike2BuyV2Items =>
  normalize(toLike2BuyV2MediaItems(mediaItems), (item) => item.cuid);

export const getLike2BuyV2StatusForRequest = (
  filter: Like2BuyV2ModerationItemFilter,
) => (filter === DEFAULT_LIKE2BUY_V2_FILTER ? undefined : filter);

export const getDoesItemNeedProducts = (item: ILike2BuyV2MediaItem) =>
  (item.mediaOverlayMetadata?.productTagsCount ?? 0) === 0;

export const getDoesItemNeedRights = (item: ILike2BuyV2MediaItem) =>
  !item.rightsPermissions?.distributionChannels?.includes(
    REQUIRED_LIKE2BUY_DISTRIBUTION_CHANNEL,
  );

export const getDoesItemNeedAction = (item: ILike2BuyV2MediaItem) => {
  const needsProducts = getDoesItemNeedProducts(item);
  const needsRights = getDoesItemNeedRights(item);

  return needsProducts || needsRights;
};

export const getCanItemBePinned = (item: ILike2BuyV2MediaItem) =>
  isUndefined(item.like2BuyV2Metadata?.pinnedPosition) &&
  !getDoesItemNeedAction(item);

export const getTooltipContent = (
  item: ILike2BuyV2MediaItem,
  hasMaxPins: boolean,
) => {
  if (hasMaxPins && getCanItemBePinned(item)) {
    return 'Maximum number of items pinned. Unpin items to pin more.';
  } else if (getDoesItemNeedAction(item)) {
    return 'This media requires action. Check if it needs products tagged or rights requested.';
  }
};

export const getDoesItemNeedRightsAndProducts = (
  item: ILike2BuyV2MediaItem,
) => {
  const needsProducts = getDoesItemNeedProducts(item);
  const needsRights = getDoesItemNeedRights(item);

  return needsProducts && needsRights;
};

interface IPinUnpinConfig {
  isDisabled: boolean;
  pinItem: (event: React.MouseEvent) => void;
  tooltipContent?: string;
  trackingId: string;
  unpinItem: (event: React.MouseEvent) => void;
}

interface IArchiveConfig {
  archiveItem: (event: React.MouseEvent) => void;
  isDisabled: boolean;
  trackingId: string;
  unarchiveItem: (event: React.MouseEvent) => void;
}

interface IActionSheetConfig {
  archiveConfig: IArchiveConfig;
  item: ILike2BuyV2MediaItem;
  pinUnpinConfig: IPinUnpinConfig;
}

const getPinUnpinConfig = (
  item: ILike2BuyV2MediaItem,
  pinUnpinConfig: IPinUnpinConfig,
): IActionSheetButton => {
  const { isDisabled, unpinItem, pinItem, tooltipContent, trackingId } =
    pinUnpinConfig;

  const isPinned =
    item.like2BuyV2ModerationStatus === Like2BuyV2ModerationStatus.Pinned;

  return {
    isDisabled: isDisabled,
    label: isPinned ? 'Unpin' : 'Pin',
    onClick: isPinned ? unpinItem : pinItem,
    tooltipContent: tooltipContent,
    trackingId: trackingId,
  };
};

const getArchiveConfig = (
  item: ILike2BuyV2MediaItem,
  archiveConfig: IArchiveConfig,
): IActionSheetButton => {
  const { isDisabled, unarchiveItem, archiveItem, trackingId } = archiveConfig;

  const isArchived =
    item.like2BuyV2ModerationStatus === Like2BuyV2ModerationStatus.Archived;

  return {
    isDisabled: isDisabled,
    label: isArchived ? 'Unarchive' : 'Archive',
    onClick: isArchived ? unarchiveItem : archiveItem,
    trackingId: trackingId,
  };
};

export const getActionSheetButtons = (
  actionSheetConfig: IActionSheetConfig,
) => {
  const { archiveConfig, item, pinUnpinConfig } = actionSheetConfig;

  return [
    getArchiveConfig(item, archiveConfig),
    getPinUnpinConfig(item, pinUnpinConfig),
  ];
};

const isStoryPost = (mediaProductType?: InstagramMediaProductType) =>
  mediaProductType === InstagramMediaProductType.STORY;
const isReelsPost = (mediaProductType?: InstagramMediaProductType) =>
  mediaProductType === InstagramMediaProductType.REELS;
const isIgtvPost = (mediaProductType?: InstagramMediaProductType) =>
  mediaProductType === InstagramMediaProductType.IGTV;

/**
 * Gets the media type overlay icon type for Like2BuyV2 media items
 * for the mediaType prop in Like2BuyV2ItemMedia component
 */
export const getMediaOverlayIconType = (
  networkItem: INetworkItem,
): Exclude<MediaTypeOverlayIconType, 'gallery' | 'gif'> => {
  if (networkItem.socialNetwork === SocialNetwork.TikTok) {
    return 'tiktok';
  }

  if (networkItem.socialNetwork === SocialNetwork.Pinterest) {
    return 'pinterest';
  }

  if (networkItem.socialNetwork === SocialNetwork.Twitter) {
    return 'twitter';
  }

  if (networkItem.socialNetwork === SocialNetwork.Instagram) {
    if (isReelsPost(networkItem.mediaProductType)) {
      return 'Reels';
    }

    if (
      networkItem.mediaType === MediaType.Video &&
      !isStoryPost(networkItem.mediaProductType)
    ) {
      return 'video';
    }

    if (isStoryPost(networkItem.mediaProductType)) {
      return 'story';
    }

    if (isIgtvPost(networkItem.mediaProductType)) {
      return 'IGTV';
    }
  }

  return networkItem.mediaType === MediaType.Video ? 'video' : 'image';
};

export function getPinnedItemNowHasNoProduct(
  initialItem: ILike2BuyV2MediaItem,
  updatedItem: ILike2BuyV2MediaItem,
) {
  return (
    initialItem.like2BuyV2ModerationStatus ===
      Like2BuyV2ModerationStatus.Pinned &&
    updatedItem.like2BuyV2ModerationStatus ===
      Like2BuyV2ModerationStatus.NeedsProducts
  );
}

export function getHasItemStatusChanged(
  initialItem: ILike2BuyV2MediaItem,
  updatedItem: ILike2BuyV2MediaItem,
) {
  return (
    (initialItem.like2BuyV2ModerationStatus ===
      Like2BuyV2ModerationStatus.NeedsProducts &&
      updatedItem.like2BuyV2ModerationStatus ===
        Like2BuyV2ModerationStatus.Published) ||
    (initialItem.like2BuyV2ModerationStatus ===
      Like2BuyV2ModerationStatus.Pinned &&
      updatedItem.like2BuyV2ModerationStatus ===
        Like2BuyV2ModerationStatus.NeedsProducts) ||
    (initialItem.like2BuyV2ModerationStatus ===
      Like2BuyV2ModerationStatus.Published &&
      updatedItem.like2BuyV2ModerationStatus ===
        Like2BuyV2ModerationStatus.NeedsProducts)
  );
}

export type ILike2BuyCustomizations =
  | ITikTokLike2BuyCustomizations
  | IInstagramLike2BuyV2Customizations;

export const storeConfigToCustomizations = (
  storeConfig: Like2BuyV2TemplateConfig,
) => {
  switch (storeConfig['@type']) {
    case 'v1:instagram-default': {
      return instagramStoreConfigToCustomizations(storeConfig);
    }
    case 'v1:tiktok-default': {
      return tiktokStoreConfigToCustomizations(storeConfig);
    }
  }
};

export const getLike2BuyV2CustomizationsToStoreConfig = (
  existingStoreConfig: Like2BuyV2TemplateConfig,
  currentCustomizations: ILike2BuyCustomizations,
  response: {
    crawlId: string;
    url: string;
  },
) => {
  if (
    isInstagramLike2BuyV2Template(existingStoreConfig) &&
    isInstagramLike2BuyV2Customizations(
      existingStoreConfig,
      currentCustomizations,
    )
  ) {
    return instagramCustomizationsToStoreConfig(existingStoreConfig, {
      ...currentCustomizations,
      banner: {
        ...currentCustomizations.banner,
        crawlId: response.crawlId,
        destinationUrl: {
          text: response.url,
        },
      },
    });
  } else {
    if (
      isTiktokLike2BuyV2Template(existingStoreConfig) &&
      isTiktokLike2BuyV2Customizations(
        existingStoreConfig,
        currentCustomizations,
      )
    ) {
      return tiktokCustomizationsToStoreConfig(existingStoreConfig, {
        ...currentCustomizations,
        banner: {
          ...currentCustomizations.banner,
          crawlId: response.crawlId,
          destinationUrl: {
            text: response.url,
          },
        },
      });
    }
  }
};

export const getLike2BuyV2PreviewConfigFromCustomizations = (
  existingStoreConfig: Like2BuyV2TemplateConfig,
  currentCustomizations: ILike2BuyCustomizations,
) => {
  if (!isDefined(existingStoreConfig)) {
    return;
  }

  if (
    isInstagramLike2BuyV2Template(existingStoreConfig) &&
    isInstagramLike2BuyV2Customizations(
      existingStoreConfig,
      currentCustomizations,
    )
  ) {
    const updatedConfig = instagramCustomizationsToStoreConfig(
      existingStoreConfig,
      currentCustomizations,
    );

    // Certain customizations need default values for Preview purposes,
    // but they aren't values that we would want to allow to be missing
    // in the actual consumer experience
    return {
      ...updatedConfig,
      ...(!!updatedConfig.galleryBannerSection?.isEnabled && {
        galleryBannerSection: {
          ...updatedConfig.galleryBannerSection,
          crawlId: updatedConfig.galleryBannerSection.crawlId ?? '',
        },
      }),
    };
  } else {
    if (
      isTiktokLike2BuyV2Template(existingStoreConfig) &&
      isTiktokLike2BuyV2Customizations(
        existingStoreConfig,
        currentCustomizations,
      )
    ) {
      const updatedConfig = tiktokCustomizationsToStoreConfig(
        existingStoreConfig,
        currentCustomizations,
      );

      // Certain customizations need default values for Preview purposes,
      // but they aren't values that we would want to allow to be missing
      // in the actual consumer experience
      return {
        ...updatedConfig,
        ...(!!updatedConfig.galleryBannerSection?.isEnabled && {
          galleryBannerSection: {
            ...updatedConfig.galleryBannerSection,
            crawlId: updatedConfig.galleryBannerSection.crawlId ?? '',
          },
        }),
      };
    }
  }
};

// We use the same permission as Like2Buy v1
export const L2B_V2_MODERATE_TASK = 'like2buy:moderate';
export const hasLike2BuyV2ModerationPermission = () =>
  hasPermissionForTask(L2B_V2_MODERATE_TASK);
